import { type ForwardedRef, forwardRef, useEffect } from 'react';

import {
  type ListHubspotPipelinesQuery,
  type ListHubspotPipelinesQueryVariables,
  useListHubspotPipelinesQuery,
} from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

type Variables = Partial<Omit<ListHubspotPipelinesQueryVariables, 'pageNumber' | 'pageSize'>>;
type Data = ComboboxItemWithFormatted &
  Omit<ListHubspotPipelinesQuery['listHubspotPipelines']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<Variables>;

const useQuery = (
  variables: Variables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListHubspotPipelinesQueryVariables, Data> => {
  const { data, ...rest } = useListHubspotPipelinesQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      enabled: variables?.enabled ?? null,
      excludeIds: variables?.excludeIds ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listHubspotPipelines?.items?.map(it => ({
        value: it.id,
        label: it.name,
        disabled: !it.enabled,
        useBIResultByDefault: it.useBIResultByDefault,
        updatedAt: it.updatedAt,
      })) || [],
    ...rest,
  };
};

export const HubspotPipelineSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);
